import { OpportunitiesApi } from '@/api';
import { SELECT_SEARCH_CHAR_THRESHOLD } from '@/main';
import {
  Empty,
  GeekItem,
  GeekItemWithRole,
  DateRange,
} from '@/models';
import { OpportunityNewsItem } from '@/models/dashboard';
import {
  OpportunityListItem,
  Opportunity,
  Department,
  Tag,
  LevelEntity,
  Counter,
  NewOpportunity,
  OpportunityMember,
} from '@/models/opportunities';
import { OpportunitiesCount } from '@/models/geek-profile';
import { FinalizationFormData } from '@/models/opportunities/form-data';
import { Getters } from '@/store/getters';
import { resetModel } from '@/utils';
import { Module } from 'vuex';
import { Actions } from './actions';
import { Mutations } from './mutations';
import { RootState } from './root.store';
import { OpportunitiesDraftsState, OpportunitiesDraftsModule } from './opportunities-drafts.module';

const opportunitiesApi = new OpportunitiesApi();

export interface OpportunitiesState {
  opportunities: OpportunityListItem[];
  news: OpportunityNewsItem[];
  current: Opportunity;
  formData: FinalizationFormData;
  departments: Department[];
  tags: Tag[];
  popularTags: Tag[];
  levels: LevelEntity[];
  geeksWithRoles: GeekItemWithRole[];
  candidates: GeekItem[];
  pagination: {
    offset: number,
    limit: number,
    totalCount: number,
  };
  counters: Counter[];
  creators: GeekItem[];
  issuers: GeekItem[];
  approvers: GeekItem[];
  admins: GeekItem[];
  opportunitiesCount: OpportunitiesCount;
  drafts: OpportunitiesDraftsState;
}

export const OpportunitiesModule: Module<OpportunitiesState, RootState> = {
  state: {
    opportunities: [] as OpportunityListItem[],
    news: [] as OpportunityNewsItem[],
    current: Empty.opportunity as Opportunity,
    formData: {
      summary: '',
      geeksAward: [],
    } as FinalizationFormData,
    departments: [] as Department[],
    tags: [] as Tag[],
    popularTags: [] as Tag[],
    levels: [] as LevelEntity[],
    geeksWithRoles: [] as GeekItemWithRole[],
    candidates: [] as GeekItem[],
    pagination: {
      offset: 0,
      limit: 50,
      totalCount: 0,
    },
    counters: [] as Counter[],
    creators: [] as GeekItem[],
    issuers: [] as GeekItem[],
    approvers: [] as GeekItem[],
    admins: [] as GeekItem[],
    opportunitiesCount: {
      opportunitiesCount: 0,
    },
    drafts: OpportunitiesDraftsModule.state as OpportunitiesDraftsState,
  },

  modules: {
    opportunitiesDrafts: OpportunitiesDraftsModule,
  },

  mutations: {
    [Mutations.opportunities.setOpportunities](state, opportunities: OpportunityListItem[]) {
      state.opportunities = opportunities;
    },
    [Mutations.opportunities.addOpportunities](state, opportunities: OpportunityListItem[]) {
      state.opportunities.push(...opportunities);
    },
    [Mutations.opportunities.setNews](state, opportunitiesNews: OpportunityNewsItem[]) {
      state.news = opportunitiesNews;
    },
    [Mutations.opportunities.setCurrent](state, opportunity: Opportunity) {
      state.current = opportunity;
    },
    [Mutations.opportunities.setFinalizationFormData](state, formData: FinalizationFormData) {
      state.formData = formData;
    },
    [Mutations.opportunities.setDepartments](state, departments: Department[]) {
      state.departments = departments;
    },
    [Mutations.opportunities.setLevels](state, levels: LevelEntity[]) {
      state.levels = levels;
    },
    [Mutations.opportunities.setTags](state, tags: Tag[]) {
      state.tags = tags;
    },
    [Mutations.opportunities.setPopularTags](state, tags: Tag[]) {
      state.popularTags = tags;
    },
    [Mutations.opportunities.setGeeksWithRoles](state, geeksWithRoles: GeekItemWithRole[]) {
      state.geeksWithRoles = geeksWithRoles;
    },
    [Mutations.opportunities.setCandidates](state, candidates: GeekItem[]) {
      state.candidates = candidates;
    },
    [Mutations.opportunities.resetPagination](state) {
      state.pagination = resetModel(state.pagination);
    },
    [Mutations.opportunities.setElementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.opportunities.incrementPagination](state) {
      state.pagination.offset += 20;
    },
    [Mutations.opportunities.addGeeksWithRoles](state, geeksWithRoles: GeekItemWithRole[]) {
      state.geeksWithRoles.push(...geeksWithRoles);
    },
    [Mutations.opportunities.setCounters](state, counters: Counter[]) {
      state.counters = counters;
    },
    [Mutations.opportunities.setCreators](state, creators: GeekItem[]) {
      state.creators = creators;
    },
    [Mutations.opportunities.setIssuers](state, issuers: GeekItem[]) {
      state.issuers = issuers;
    },
    [Mutations.opportunities.setApprovers](state, approvers: GeekItem[]) {
      state.approvers = approvers;
    },
    [Mutations.opportunities.setAdmins](state, admins: GeekItem[]) {
      state.admins = admins;
    },
    [Mutations.opportunities.setGeekOpportunitiesCount](state, opportunitiesCount: OpportunitiesCount) {
      state.opportunitiesCount = opportunitiesCount;
    },
    [Mutations.opportunities.setNewMember](state, members: OpportunityMember[]) {
      state.current.members = members;
    },
    [Mutations.opportunities.resetCandidates](state) {
      state.candidates = [];
    },
  },

  getters: {
    [Getters.opportunities.filteredCandidates](state) {
      return state.candidates
        .reduce((filtered: GeekItem[], geek: GeekItem) => {
          if (state.current.members.some(m => m.geek.id === geek.id)) {
            return filtered;
          }

          if (geek.id === state.current.issuer.id) {
            return filtered;
          }

          filtered.push(geek);
          return filtered;
        }, []);
    },
  },

  actions: {
    async [Actions.opportunities.fetchOpportunities]({ commit, state }, filters): Promise<void> {
      commit(Mutations.opportunities.resetPagination);
      const opportunities = await opportunitiesApi.fetchOpportunities(filters, state.pagination.offset);
      commit(Mutations.opportunities.setOpportunities, opportunities.data);
      commit(Mutations.opportunities.setElementsCount, opportunities.totalCount);
    },
    async [Actions.opportunities.fetchMoreOpportunities]({ commit, state }, filters): Promise<void> {
      commit(Mutations.opportunities.incrementPagination);
      const opportunities = await opportunitiesApi.fetchOpportunities(filters, state.pagination.offset);
      commit(Mutations.opportunities.addOpportunities, opportunities.data);
      commit(Mutations.opportunities.setElementsCount, opportunities.totalCount);
    },
    async [Actions.opportunities.fetchNews]({ commit }): Promise<void> {
      commit(Mutations.opportunities.resetPagination);
      const opportunitiesNews = await opportunitiesApi.fetchNews();
      commit(Mutations.opportunities.setNews, opportunitiesNews.data);
      commit(Mutations.opportunities.setElementsCount, opportunitiesNews.totalCount);
    },
    async [Actions.opportunities.fetchCurrent]({ commit }, id: string): Promise<void> {
      const opportunity = await opportunitiesApi.fetchOpportunity(id);
      commit(Mutations.opportunities.setCurrent, opportunity);
    },
    async [Actions.opportunities.fetchFinalizationDraft]({ commit }, id: string): Promise<void> {
      const finalizationDraft = await opportunitiesApi.fetchFinalizationDraft(id);
      commit(Mutations.opportunities.setFinalizationFormData, finalizationDraft);
    },
    async [Actions.opportunities.keepFinalizationDraft](_, { opportunityId, formData }): Promise<void> {
      return await opportunitiesApi.keepFinalizationDraft(opportunityId, formData);
    },
    async [Actions.opportunities.editFinalizationDraft](_, { opportunityId, formData }): Promise<void> {
      return await opportunitiesApi.editFinalizationDraft(opportunityId, formData);
    },
    async [Actions.opportunities.changeMembershipStatus](_, { id, ...data }): Promise<void> {
      return await opportunitiesApi.changeMembershipStatus(id, data);
    },
    async [Actions.opportunities.changeCandidateStatus](_, { opportunityId, geekId, status, reason }): Promise<void> {
      return await opportunitiesApi.changeCandidateStatus(opportunityId, geekId, status, reason);
    },
    async [Actions.opportunities.approveOpportunity](_, opportunityId): Promise<void> {
      return await opportunitiesApi.approveOpportunity(opportunityId);
    },
    async [Actions.opportunities.sendMessage](_, { opportunityId, message }): Promise<void> {
      return await opportunitiesApi.sendMessage(opportunityId, message);
    },
    async [Actions.opportunities.giveFeedback](_, { opportunityId, message }): Promise<void> {
      return await opportunitiesApi.giveFeedback(opportunityId, message);
    },
    async [Actions.opportunities.finalizeOpportunity](_, { opportunityId, formData }): Promise<void> {
      return await opportunitiesApi.finalizeOpportunity(opportunityId, formData);
    },
    async [Actions.opportunities.rejectOpportunity](_, { opportunityId, reason }): Promise<void> {
      return await opportunitiesApi.rejectOpportunity(opportunityId, reason);
    },
    async [Actions.opportunities.cancelRecruitingOpportunity](_, { opportunityId, reason }): Promise<void> {
      return await opportunitiesApi.cancelRecruitingOpportunity(opportunityId, reason);
    },
    async [Actions.opportunities.cancelOngoingOpportunity](_, { opportunityId, formData }): Promise<void> {
      return await opportunitiesApi.cancelOngoingOpportunity(opportunityId, formData);
    },
    async [Actions.opportunities.launchOpportunity](_, opportunityId): Promise<void> {
      return await opportunitiesApi.launchOpportunity(opportunityId);
    },
    async [Actions.opportunities.deleteOpportunity](_, { opportunityId, reason }): Promise<void> {
      return await opportunitiesApi.deleteOpportunity(opportunityId, reason);
    },
    async [Actions.opportunities.fetchDepartments]({ commit }): Promise<void> {
      const departments = await opportunitiesApi.fetchDepartments();
      commit(Mutations.opportunities.setDepartments, departments);
    },
    async [Actions.opportunities.fetchLevels]({ commit }): Promise<void> {
      const levels = await opportunitiesApi.fetchLevels();
      commit(Mutations.opportunities.setLevels, levels);
    },
    async [Actions.opportunities.fetchTags]({ commit }): Promise<void> {
      const tags = await opportunitiesApi.fetchTags();
      commit(Mutations.opportunities.setTags, tags);
    },
    async [Actions.opportunities.fetchPopularTags]({ commit }): Promise<void> {
      const popularTags = await opportunitiesApi.fetchPopularTags();
      commit(Mutations.opportunities.setPopularTags, popularTags);
    },
    async [Actions.opportunities.create](_, newOpportunity: NewOpportunity): Promise<Opportunity> {
      return await opportunitiesApi.createOpportunity(newOpportunity);
    },
    async [Actions.opportunities.edit](_, opportunity: NewOpportunity & {id: string}): Promise<void> {
      return await opportunitiesApi.editOpportunity(opportunity);
    },
    async [Actions.opportunities.fetchGeeksWithRoles]({ commit }, filters): Promise<void> {
      commit(Mutations.opportunities.resetPagination);
      const response = await opportunitiesApi.fetchGeeksWithRoles(filters);
      commit(Mutations.opportunities.setGeeksWithRoles, response.data);
      commit(Mutations.opportunities.setElementsCount, response.totalCount);
    },
    async [Actions.opportunities.fetchMoreGeeksWithRoles]({ commit, state }, filters): Promise<void> {
      commit(Mutations.opportunities.incrementPagination);
      const response = await opportunitiesApi.fetchGeeksWithRoles(
        filters,
        state.pagination.offset,
      );
      commit(Mutations.opportunities.addGeeksWithRoles, response.data);
      commit(Mutations.opportunities.setElementsCount, response.totalCount);
    },
    async [Actions.opportunities.fetchCandidates]({ commit }, searchPhrase: string) {
      if (searchPhrase.length < SELECT_SEARCH_CHAR_THRESHOLD) {
        commit(Mutations.opportunities.resetCandidates);
        return;
      }
      const candidates = await opportunitiesApi.fetchCandidates(searchPhrase);
      commit(Mutations.opportunities.setCandidates, candidates);
    },
    async [Actions.opportunities.filterCandidates]({ commit }, searchPhrase: string) {
      commit(Mutations.opportunities.setCandidatesSearchPhrase, searchPhrase);
    },
    async [Actions.opportunities.addMember]({ commit, state }, member: GeekItem) {
      const members = await opportunitiesApi.addMember(state.current.id, member.id);
      commit(Mutations.opportunities.setNewMember, members);
    },
    async [Actions.opportunities.changeGeekRole](_, { geekWithRole, role }): Promise<void> {
      return await opportunitiesApi.changeGeekRole(geekWithRole, role);
    },
    async [Actions.opportunities.fetchCounters]({ commit }): Promise<void> {
      const counters = await opportunitiesApi.fetchOpportunitiesCounters();
      commit(Mutations.opportunities.setCounters, counters);
    },
    async [Actions.opportunities.fetchCreators]({ commit }): Promise<void> {
      const creators = await opportunitiesApi.fetchCreators();
      commit(Mutations.opportunities.setCreators, creators);
    },
    async [Actions.opportunities.fetchIssuers]({ commit }): Promise<void> {
      const issuers = await opportunitiesApi.fetchIssuers();
      commit(Mutations.opportunities.setIssuers, issuers);
    },
    async [Actions.opportunities.fetchApprovers]({ commit }): Promise<void> {
      const approvers = await opportunitiesApi.fetchApprovers();
      commit(Mutations.opportunities.setApprovers, approvers);
    },
    async [Actions.opportunities.fetchAdmins]({ commit }): Promise<void> {
      const admins = await opportunitiesApi.fetchAdmins();
      commit(Mutations.opportunities.setAdmins, admins);
    },
    async [Actions.opportunities.fetchGeeksCounters]({ commit }): Promise<void> {
      const counters = await opportunitiesApi.fetchGeeksCounters();
      commit(Mutations.opportunities.setCounters, counters);
    },
    async [Actions.opportunities.fetchGeekOpportunitiesCount]({ commit }, geekId: string): Promise<void> {
      const geekOpportunitiesCount = await opportunitiesApi.fetchGeekOpportunitiesCount(geekId);
      commit(Mutations.opportunities.setGeekOpportunitiesCount, geekOpportunitiesCount);
    },
    async [Actions.opportunities.fetchReport](_, dateRange: DateRange) {
      return await opportunitiesApi.fetchReport(dateRange);
    },
    [Actions.opportunities.resetCandidates]({ commit }) {
      commit(Mutations.opportunities.resetCandidates);
    },
  },
};
